import React, { useEffect, useCallback, useState } from "react";
import { Box, Paper, Button, IconButton, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CandidateTable, { COLUMNS_CONSTANTS as CANDIDATE_COLUMNS } from "./candidate/CandidateTable";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useCandidateModal } from "bundles/Internal/components/common/CandidateModalProvider";
import NotesDrawer from "bundles/Internal/components/common/NotesDrawer";
import ApplyToOfferDialog from "bundles/Internal/components/common/ApplyToOfferDialog";
import ConfirmDialog from "bundles/Internal/components/common/ConfirmDialog";
import exportCsv from "bundles/common/utils/exportCsv";
import { appendFormValue } from "bundles/Internal/utils/candidateFormData";
import {
  EMPTY_FILTERS,
  EXPERIENCE_YEAR_OPTIONS,
  PROFILE_COMPLETION_OPTIONS,
  LANGUAGE_OPTIONS,
} from "bundles/Internal/components/candidate/CandidateTableFilters";

function expandRangeValues(selectedValues, options) {
  const nums = [];
  for (const val of selectedValues) {
    const opt = options.find((o) => o.value === val);
    if (opt) {
      for (let i = opt.gte; i <= opt.lte; i++) nums.push(i);
    }
  }
  return nums;
}

export function buildSearchFilters(activeFilters) {
  const filters = {};
  let hasAny = false;

  if (activeFilters.sector?.length) {
    filters.desired_sector = activeFilters.sector;
    hasAny = true;
  }

  if (activeFilters.region?.length) {
    filters.region = activeFilters.region;
    hasAny = true;
  }

  if (activeFilters.languages?.length) {
    filters.preferred_locale = activeFilters.languages.flatMap(
      (l) => LANGUAGE_OPTIONS.find((o) => o.value === l)?.locales || [l]
    );
    hasAny = true;
  }

  if (activeFilters.experience_year?.length) {
    filters.experience_year = expandRangeValues(activeFilters.experience_year, EXPERIENCE_YEAR_OPTIONS);
    hasAny = true;
  }

  if (activeFilters.profile_completion?.length) {
    filters.profile_completion = expandRangeValues(activeFilters.profile_completion, PROFILE_COMPLETION_OPTIONS);
    hasAny = true;
  }

  if (activeFilters.created_by?.length) {
    filters.creator_id = activeFilters.created_by;
    hasAny = true;
  }

  return hasAny ? filters : null;
}

const CandidatesIndexPage = ({ scope } = {}) => {
  const [candidates, setCandidates] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const { openCreateCandidate } = useCandidateModal();
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesCandidate, setNotesCandidate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);

  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  const [bulkNoteDialogOpen, setBulkNoteDialogOpen] = useState(false);
  const [bulkNoteContent, setBulkNoteContent] = useState("");
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [applyToJobDialogOpen, setApplyToJobDialogOpen] = useState(false);
  const [confirmBlacklistOpen, setConfirmBlacklistOpen] = useState(false);
  const [followedCandidateIds, setFollowedCandidateIds] = useState(() => new Set());
  const [creators, setCreators] = useState([]);

  const isToBeReviewedScope = scope === "to_be_reviewed";

  useEffect(() => {
    sh.get("/internal/follows")
      .then((res) => setFollowedCandidateIds(new Set(res.data?.Candidate || [])))
      .catch(() => {});
  }, []);

  useEffect(() => {
    sh.get("/internal/creators", { params: { type: "Candidate" } })
      .then((res) => setCreators(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  const handleToggleFollow = useCallback((candidateId, next) => {
    setFollowedCandidateIds((prev) => {
      const updated = new Set(prev);
      if (next) updated.add(candidateId);
      else updated.delete(candidateId);
      return updated;
    });
  }, []);

  const fetchCandidates = useCallback((query = "", pageNum = 1, limit = 25, activeFilters = {}) => {
    const hasQuery = Boolean(String(query || "").trim());
    const searchFilters = buildSearchFilters(activeFilters);
    const hasFilters = searchFilters !== null;

    setLoading(true);
    sh.get("/internal/search/candidates", {
      params: {
        query: hasQuery ? query : undefined,
        page: pageNum,
        limit,
        scope: scope || undefined,
        ...(hasFilters && { filters: JSON.stringify(searchFilters) }),
      },
    })
      .then((res) => {
        const payload = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setCandidates(payload);
        if (res.data?.pagy) {
          setTotalCount(res.data.pagy.count || payload.length);
        } else {
          setTotalCount(payload.length);
        }
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || String(err?.message || "").toLowerCase().includes("canceled")) return;
        enqueueSnackbar("Failed to fetch candidates", { variant: "error" });
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar, scope]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchCandidates(searchQuery, 1, rowsPerPage, filters);
    }, 250);
    return () => clearTimeout(t);
  }, [fetchCandidates, searchQuery, rowsPerPage, filters]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    fetchCandidates(searchQuery, newPage, rowsPerPage, filters);
  }, [fetchCandidates, searchQuery, rowsPerPage, filters]);

  const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleSelectCandidate = useCallback((candidateId, checked) => {
    setSelectedCandidateIds((prev) =>
      checked ? [...prev, candidateId] : prev.filter((id) => id !== candidateId)
    );
  }, []);

  const handleSelectAll = useCallback((checked) => {
    setSelectedCandidateIds(checked ? candidates.map((c) => c.id) : []);
  }, [candidates]);

  const handleBulkUpdateStatus = useCallback(async (whitelisted) => {
    if (selectedCandidateIds.length === 0) return;

    setBulkActionLoading(true);
    try {
      await sh.post("/internal/candidates/bulk_update_status", {
        authenticity_token,
        candidate_ids: selectedCandidateIds,
        whitelisted,
      });
      enqueueSnackbar(`${selectedCandidateIds.length} candidates updated`, { variant: "success" });
      setSelectedCandidateIds([]);
      fetchCandidates(searchQuery, page, rowsPerPage, filters);
    } catch (err) {
      enqueueSnackbar("Failed to update candidates", { variant: "error" });
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedCandidateIds, authenticity_token, enqueueSnackbar, fetchCandidates, searchQuery, page, rowsPerPage, filters]);

  const handleBulkAddNote = useCallback(async () => {
    if (!bulkNoteContent.trim()) {
      enqueueSnackbar("Note content cannot be blank", { variant: "error" });
      return;
    }

    setBulkActionLoading(true);
    try {
      await sh.post("/internal/candidates/bulk_add_note", {
        authenticity_token,
        candidate_ids: selectedCandidateIds,
        note_content: bulkNoteContent,
      });
      enqueueSnackbar(`Note added to ${selectedCandidateIds.length} candidates`, { variant: "success" });
      setBulkNoteDialogOpen(false);
      setBulkNoteContent("");
      setSelectedCandidateIds([]);
      fetchCandidates(searchQuery, page, rowsPerPage, filters);
    } catch (err) {
      enqueueSnackbar("Failed to add note", { variant: "error" });
    } finally {
      setBulkActionLoading(false);
    }
  }, [bulkNoteContent, selectedCandidateIds, authenticity_token, enqueueSnackbar, fetchCandidates, searchQuery, page, rowsPerPage, filters]);

  const handleBulkApplySuccess = useCallback(() => {
    setSelectedCandidateIds([]);
    setApplyToJobDialogOpen(false);
  }, []);

  const handleExportCsv = useCallback(() => {
    exportCsv("candidates", selectedCandidateIds, authenticity_token, CANDIDATE_COLUMNS)
      .then(() => enqueueSnackbar("CSV exported", { variant: "success" }))
      .catch(() => enqueueSnackbar("Failed to export CSV", { variant: "error" }));
  }, [selectedCandidateIds, authenticity_token, enqueueSnackbar]);

  const createCandidate = (values, { reset, resumeFile, autoEmail, close } = {}) => {
    if (!resumeFile) {
      return sh.post("/internal/candidates", {
        candidate: values,
        authenticity_token,
        auto_email: autoEmail,
      })
        .then((res) => {
          setCandidates((prev) => [res.data, ...prev]);
          enqueueSnackbar("Candidate created", { variant: "success" });
          reset?.();
          close?.();
        })
        .catch((err) => {
          const messages = err?.response?.data?.errors;
          if (err?.response?.status === 422 && Array.isArray(messages) && messages.length > 0) {
            messages.forEach((msg) => enqueueSnackbar(msg, { variant: "error" }));
            return;
          }
          enqueueSnackbar("Failed to create candidate", { variant: "error" });
        });
    }

    const formData = new FormData();
    formData.append("authenticity_token", authenticity_token);

    const appendValue = (key, value) => appendFormValue(formData, key, value);

    Object.entries(values || {}).forEach(([k, v]) => appendValue(`candidate[${k}]`, v));
    formData.append("candidate[resume]", resumeFile);

    if (autoEmail && typeof autoEmail === "object") {
      Object.entries(autoEmail).forEach(([k, v]) => appendValue(`auto_email[${k}]`, v));
    }

    return sh.post("/internal/candidates", formData)
      .then((res) => {
        setCandidates((prev) => [res.data, ...prev]);
        enqueueSnackbar("Candidate created", { variant: "success" });
        reset?.();
        close?.();
      })
      .catch((err) => {
        const messages = err?.response?.data?.errors;
        if (err?.response?.status === 422 && Array.isArray(messages) && messages.length > 0) {
          messages.forEach((msg) => enqueueSnackbar(msg, { variant: "error" }));
          return;
        }
        enqueueSnackbar("Failed to create candidate", { variant: "error" });
      });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #F9FAFF 0%, #EEF0F6 100%)",
      }}
    >
      <CandidateTable
        candidates={candidates}
        onOpenCreateCandidate={() => openCreateCandidate({ onSubmit: createCandidate })}
        onApplyToJob={() => setApplyToJobDialogOpen(true)}
        onQueryChange={(q) => setSearchQuery(q)}
        filters={filters}
        onFiltersChange={setFilters}
        creators={creators}
        onOpenNotes={(candidate) => {
          setNotesCandidate(candidate);
          setNotesOpen(true);
        }}
        onCandidateSelect={(candidate) => {
          if (!candidate?.id) return;
          navigate(`/internal/candidates/${candidate.id}`, { state: { candidate, from: location.pathname } });
        }}
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={loading}
        selectedCandidateIds={selectedCandidateIds}
        onSelectCandidate={handleSelectCandidate}
        onSelectAll={handleSelectAll}
        followedCandidateIds={followedCandidateIds}
        onToggleFollow={handleToggleFollow}
      />

      {/* Floating Action Bar */}
      {selectedCandidateIds.length > 0 && (
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              px: 3,
              py: 1.5,
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              borderRadius: "50px",
              backgroundColor: "#fff",
              border: "1px solid hsl(140 10% 88%)",
            }}
          >
            <Typography sx={{ color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, system-ui, sans-serif", fontSize: 14, mr: 1 }}>
              {selectedCandidateIds.length} selected
            </Typography>
            <Box sx={{ width: "1px", height: 24, backgroundColor: "hsl(140 10% 88%)" }} />
            {isToBeReviewedScope ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleBulkUpdateStatus(true)}
                disabled={bulkActionLoading}
                sx={{
                  textTransform: "none",
                  borderRadius: "20px",
                  borderColor: "hsl(150 38% 17%)",
                  color: "hsl(150 38% 17%)",
                  "&:hover": { backgroundColor: "hsl(150 38% 17%)", color: "#fff", borderColor: "hsl(150 38% 17%)" },
                }}
              >
                Whitelist
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setConfirmBlacklistOpen(true)}
                disabled={bulkActionLoading}
                sx={{
                  textTransform: "none",
                  borderRadius: "20px",
                  borderColor: "hsl(0 70% 50%)",
                  color: "hsl(0 70% 50%)",
                  "&:hover": { backgroundColor: "hsl(0 70% 50%)", color: "#fff", borderColor: "hsl(0 70% 50%)" },
                }}
              >
                Blacklist
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => setBulkNoteDialogOpen(true)}
              disabled={bulkActionLoading}
              sx={{
                textTransform: "none",
                borderRadius: "20px",
                borderColor: "hsl(140 10% 60%)",
                color: "hsl(140 10% 30%)",
                "&:hover": { backgroundColor: "hsl(140 10% 95%)", borderColor: "hsl(140 10% 60%)" },
              }}
            >
              Add Note
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => setApplyToJobDialogOpen(true)}
              disabled={bulkActionLoading}
              sx={{
                textTransform: "none",
                borderRadius: "20px",
                backgroundColor: "hsl(150 38% 17%)",
                "&:hover": { backgroundColor: "hsl(150 38% 22%)" },
              }}
            >
              Apply to Job
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleExportCsv}
              disabled={bulkActionLoading}
              sx={{
                textTransform: "none",
                borderRadius: "20px",
                borderColor: "hsl(150 38% 17%)",
                color: "hsl(150 38% 17%)",
                "&:hover": { backgroundColor: "hsl(150 38% 17%)", color: "#fff", borderColor: "hsl(150 38% 17%)" },
              }}
            >
              Export CSV
            </Button>
            <IconButton
              size="small"
              onClick={() => setSelectedCandidateIds([])}
              sx={{ ml: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Paper>
        </Box>
      )}

      <NotesDrawer
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        resource="candidates"
        record={notesCandidate}
        onNoteAdded={(candidateId) => {
          setCandidates((prev) =>
            prev.map((c) =>
              String(c?.id) === String(candidateId)
                ? { ...c, notes_count: (Number(c.notes_count) || 0) + 1 }
                : c
            )
          );
        }}
        onNoteArchived={(candidateId) => {
          setCandidates((prev) =>
            prev.map((c) =>
              String(c?.id) === String(candidateId)
                ? { ...c, notes_count: Math.max((Number(c.notes_count) || 0) - 1, 0) }
                : c
            )
          );
        }}
      />

      {/* Bulk Note Dialog */}
      <Dialog
        open={bulkNoteDialogOpen}
        onClose={() => setBulkNoteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: "12px", minWidth: 400, maxWidth: 500 } }}
      >
        <DialogTitle
          sx={{
            fontSize: 16,
            fontWeight: 600,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            color: "hsl(150 30% 12%)",
            pb: 0.5,
          }}
        >
          Add Note to {selectedCandidateIds.length} Candidate{selectedCandidateIds.length > 1 ? "s" : ""}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            value={bulkNoteContent}
            onChange={(e) => setBulkNoteContent(e.target.value)}
            placeholder="Enter note content..."
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: 14,
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                "& fieldset": {
                  borderColor: "hsl(140 8% 88%)",
                },
                "&:hover fieldset": {
                  borderColor: "hsl(150 38% 17%)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "hsl(150 38% 17%)",
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            disableRipple
            onClick={() => {
              setBulkNoteDialogOpen(false);
              setBulkNoteContent("");
            }}
            sx={{
              textTransform: "none",
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: "hsl(150 10% 46%)",
              borderRadius: "8px",
              height: "auto",
              px: 2,
              "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
            }}
          >
            Cancel
          </Button>
          <Button
            disableRipple
            disableElevation
            variant="contained"
            onClick={handleBulkAddNote}
            disabled={!bulkNoteContent.trim() || bulkActionLoading}
            sx={{
              textTransform: "none",
              minHeight: 0,
              height: "auto",
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              borderRadius: "8px",
              px: 2,
              backgroundColor: "hsl(150 38% 17%)",
              color: "hsl(80 97% 72%)",
              "&:hover": {
                backgroundColor: "hsl(150 38% 14%)",
              },
              "&.Mui-disabled": {
                backgroundColor: "hsl(140 10% 85%)",
                color: "hsl(150 10% 60%)",
              },
            }}
          >
            Add Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Apply to Job Dialog */}
      <ApplyToOfferDialog
        open={applyToJobDialogOpen}
        onClose={() => setApplyToJobDialogOpen(false)}
        candidateIds={selectedCandidateIds}
        onApplied={handleBulkApplySuccess}
      />

      {/* Blacklist Confirmation Modal */}
      <ConfirmDialog
        open={confirmBlacklistOpen}
        onClose={() => setConfirmBlacklistOpen(false)}
        onConfirm={() => {
          setConfirmBlacklistOpen(false);
          handleBulkUpdateStatus(false);
        }}
        title="Blacklist Candidates"
        description={`Are you sure you want to blacklist ${selectedCandidateIds.length} candidate${selectedCandidateIds.length > 1 ? "s" : ""}? This action will blacklist them.`}
        confirmLabel="Blacklist"
        cancelLabel="Cancel"
        destructive={true}
      />
    </Box>
  );
};

export default CandidatesIndexPage;